import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Project, ProjectDepartment } from '@/types/project';
import { getAccessToken } from '@/utils/jwt';

interface ListProjectsParams {
  status?: string;
  page?: number;
  per_page?: number;
}

interface CreateProjectBody {
  name: string;
  code: string;
  project_type?: string;
  start_date?: string;
  end_date?: string;
  department_ids?: string[];
}

interface UpdateProjectBody {
  id: string;
  name?: string;
  code?: string;
  status?: string;
  project_type?: string;
  start_date?: string | null;
  end_date?: string | null;
}

interface ToggleDepartmentActiveBody {
  projectId: string;
  departmentId: string;
  is_active: boolean;
}

interface UnassignDepartmentParams {
  projectId: string;
  departmentId: string;
}

export const projectsApi = createApi({
  reducerPath: 'projectsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Project'],
  endpoints: (builder) => ({
    listProjects: builder.query<Project[], ListProjectsParams | undefined>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.status) searchParams.set('status', params.status);
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.per_page) searchParams.set('per_page', String(params.per_page));
        const qs = searchParams.toString();
        return `/projects${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: { success: boolean; projects: Project[] }) => response.projects,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Project' as const, id })),
              { type: 'Project', id: 'LIST' },
            ]
          : [{ type: 'Project', id: 'LIST' }],
    }),
    getProject: builder.query<Project, string>({
      query: (id) => `/projects/${id}`,
      transformResponse: (response: { success: boolean; project: Project }) => response.project,
      providesTags: (_r, _e, id) => [{ type: 'Project', id }],
    }),
    createProject: builder.mutation<Project, CreateProjectBody>({
      query: (body) => ({ url: '/projects', method: 'POST', body }),
      transformResponse: (response: { success: boolean; project: Project }) => response.project,
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),
    updateProject: builder.mutation<Project, UpdateProjectBody>({
      query: ({ id, ...body }) => ({ url: `/projects/${id}`, method: 'PUT', body }),
      transformResponse: (response: { success: boolean; project: Project }) => response.project,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),
    deleteProject: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/projects/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),
    listProjectDepartments: builder.query<ProjectDepartment[], string>({
      query: (id) => `/projects/${id}/departments`,
      transformResponse: (response: { success: boolean; departments: ProjectDepartment[] }) =>
        response.departments,
      providesTags: (_r, _e, id) => [{ type: 'Project', id }],
    }),
    assignDepartments: builder.mutation<
      ProjectDepartment[],
      { projectId: string; department_ids: string[] }
    >({
      query: ({ projectId, department_ids }) => ({
        url: `/projects/${projectId}/departments`,
        method: 'POST',
        body: { department_ids },
      }),
      transformResponse: (response: { success: boolean; departments: ProjectDepartment[] }) =>
        response.departments,
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'Project', id: projectId },
        { type: 'Project', id: 'LIST' },
      ],
    }),
    toggleDepartmentActive: builder.mutation<ProjectDepartment, ToggleDepartmentActiveBody>({
      query: ({ projectId, departmentId, is_active }) => ({
        url: `/projects/${projectId}/departments/${departmentId}`,
        method: 'PATCH',
        body: { is_active },
      }),
      transformResponse: (response: {
        success: boolean;
        department: ProjectDepartment['department'];
        is_active: boolean;
      }): ProjectDepartment => ({
        department: response.department,
        is_active: response.is_active,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [{ type: 'Project', id: projectId }],
    }),
    unassignDepartment: builder.mutation<{ success: boolean }, UnassignDepartmentParams>({
      query: ({ projectId, departmentId }) => ({
        url: `/projects/${projectId}/departments/${departmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'Project', id: projectId },
        { type: 'Project', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useListProjectDepartmentsQuery,
  useAssignDepartmentsMutation,
  useToggleDepartmentActiveMutation,
  useUnassignDepartmentMutation,
} = projectsApi;
